import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/**
 * A small set of charts, drawn as plain SVG, for the Insights screen. No chart library.
 * Rules they follow (colors are the validated palette in styles.css, `.viz`):
 *  - bars are thin (at most 24 px), rounded at the data end only, and grow from one baseline;
 *  - lines are 2 px; grids are hairlines; axis text is muted; value labels sit at the mark's end;
 *  - one series needs no legend; a part-to-whole split lists every part with its value;
 *  - every mark answers hover and keyboard focus with the same readout, and every chart can be shown as a table.
 */

/* ----------------------------------------------------------------------------- helpers */
export const fmtInt = (n: number): string => Math.round(n).toLocaleString('en-GH');
/** Pesewas as cedis, short enough for a chart label: GH₵980, GH₵12.4K, GH₵1.2M. */
export const fmtCedisShort = (pesewas: number): string => {
  const c = pesewas / 100;
  const a = Math.abs(c);
  if (a >= 1_000_000) return `GH₵${(c / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (a >= 10_000) return `GH₵${(c / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `GH₵${Math.round(c).toLocaleString('en-GH')}`;
};
export const fmtPct = (n: number): string => `${(Math.round(n * 10) / 10).toLocaleString('en-GH')}%`;

/** Round tick values for an axis that starts at zero: 0, 2, 4, 6 ... */
export function niceTicks(max: number, count = 4, whole = false): number[] {
  if (!(max > 0)) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  let step = ([1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10) as number;
  if (whole) step = Math.max(1, Math.ceil(step)); // counts of bookings or deliveries: no half-way ticks
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.999; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}

/** The pixel width of an element, kept up to date (so text is drawn at its true size on a phone). */
function useWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setW(Math.round(el.clientWidth));
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

interface TipState {
  x: number;
  y: number;
  title: string;
  rows: { label: string; value: string; color?: string }[];
}

/** One readout for every chart: values lead, names follow. Also shown on keyboard focus. */
function useTip(width: number) {
  const [tip, setTip] = useState<TipState | null>(null);
  const node = tip ? (
    <div className="viz-tip" role="status" style={{ left: tip.x, top: tip.y, transform: tip.x > width / 2 ? 'translate(calc(-100% - 12px), -50%)' : 'translate(12px, -50%)' }}>
      <div className="viz-tip-title">{tip.title}</div>
      {tip.rows.map((r) => (
        <div key={r.label} className="viz-tip-row">
          {r.color ? <span className="viz-key" style={{ background: r.color }} /> : null}
          <strong>{r.value}</strong>
          <span>{r.label}</span>
        </div>
      ))}
    </div>
  ) : null;
  return { setTip, clear: () => setTip(null), node };
}

/* ----------------------------------------------------------------------------- frame */
export interface TableView {
  head: string[];
  rows: (string | number)[][];
}

/** The card every chart lives in: a title that says what is plotted, a note, and a switch to see the numbers as a table. */
export function ChartCard({ title, note, table, children, wide = false }: { title: string; note?: ReactNode; table?: TableView; children: ReactNode; wide?: boolean }) {
  const [asTable, setAsTable] = useState(false);
  return (
    <section className={`viz-card${wide ? ' wide' : ''}`} aria-label={title}>
      <header className="viz-card-head">
        <div>
          <h3 className="viz-title">{title}</h3>
          {note ? <p className="viz-note">{note}</p> : null}
        </div>
        {table ? (
          <button type="button" className="viz-switch" aria-pressed={asTable} onClick={() => setAsTable((v) => !v)}>
            {asTable ? 'Show chart' : 'Show table'}
          </button>
        ) : null}
      </header>
      {asTable && table ? (
        <div className="viz-table-wrap">
          <table className="viz-table">
            <thead>
              <tr>
                {table.head.map((h, i) => (
                  <th key={h} className={i > 0 ? 'r' : ''}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r, i) => (
                <tr key={i}>
                  {r.map((c, j) => (
                    <td key={j} className={j > 0 ? 'r num' : ''}>
                      {typeof c === 'number' ? fmtInt(c) : c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        children
      )}
    </section>
  );
}

export function Empty({ children = 'Nothing recorded in this period.' }: { children?: ReactNode }) {
  return <p className="viz-empty">{children}</p>;
}

/** A headline number. Delta-free on purpose: the period is chosen once, above everything. */
export function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="viz-tile">
      <div className="viz-tile-label">{label}</div>
      <div className="viz-tile-value">{value}</div>
      {hint ? <div className="viz-tile-hint">{hint}</div> : null}
    </div>
  );
}

/* ----------------------------------------------------------------------------- horizontal bars */
export interface BarRow {
  label: string;
  value: number;
  /** a second line in the readout, e.g. "3 nights · GH₵900" */
  detail?: string;
}

/** A ranked list as bars: the best at the top, the value at the tip. For names too long to sit under a column. */
export function HBars({ rows, format = fmtInt, valueName = 'Total', color = 'var(--viz-1)', emptyText }: { rows: BarRow[]; format?: (n: number) => string; valueName?: string; color?: string; emptyText?: string }) {
  const [ref, width] = useWidth();
  const { setTip, clear, node } = useTip(width);
  const max = Math.max(0, ...rows.map((r) => r.value));
  if (rows.length === 0 || max === 0) return <Empty>{emptyText}</Empty>;

  const labelW = Math.max(84, Math.min(170, Math.round(width * 0.36)));
  const valueW = 62;
  const rowH = 30;
  const barH = 16;
  const area = Math.max(20, width - labelW - valueW - 8);
  const chars = Math.max(6, Math.floor(labelW / 6.6));

  return (
    <div ref={ref} className="viz-plot">
      {width > 0 ? (
        <svg width={width} height={rows.length * rowH + 4} role="img" aria-label={`${valueName}: ${rows.map((r) => `${r.label} ${format(r.value)}`).join(', ')}`}>
          {rows.map((r, i) => {
            const y = i * rowH + 2;
            const w = r.value > 0 ? Math.max(4, (r.value / max) * area) : 0;
            const show = (e: { currentTarget: Element }, clientY?: number) => {
              const box = ref.current!.getBoundingClientRect();
              const t = (e.currentTarget as SVGGElement).getBoundingClientRect();
              setTip({ x: Math.min(width - 8, labelW + w + 10), y: (clientY ?? t.top + t.height / 2) - box.top, title: r.label, rows: [{ label: valueName, value: format(r.value), color }, ...(r.detail ? [{ label: r.detail, value: '' }] : [])] });
            };
            return (
              <g key={r.label + i} tabIndex={0} className="viz-hit" onPointerMove={(e) => show(e, e.clientY)} onPointerLeave={clear} onFocus={(e) => show(e)} onBlur={clear}>
                <rect x={0} y={y} width={width} height={rowH - 2} fill="transparent" />
                <text x={labelW - 8} y={y + rowH / 2 + 3} textAnchor="end" className="viz-label">
                  {r.label.length > chars ? `${r.label.slice(0, chars - 1)}…` : r.label}
                </text>
                {w > 0 ? <path d={`M${labelW},${y + (rowH - barH) / 2 - 1} h${Math.max(0, w - 4)} a4,4 0 0 1 4,4 v${barH - 8} a4,4 0 0 1 -4,4 h${-Math.max(0, w - 4)} z`} fill={color} className="viz-mark" /> : null}
                <text x={labelW + w + 6} y={y + rowH / 2 + 3} className="viz-value">
                  {format(r.value)}
                </text>
              </g>
            );
          })}
          <line x1={labelW} x2={labelW} y1={0} y2={rows.length * rowH + 4} className="viz-axis" />
        </svg>
      ) : null}
      {node}
    </div>
  );
}

/* ----------------------------------------------------------------------------- columns */
export interface ColumnPoint {
  label: string;
  value: number;
  /** the full name for the readout, if the axis label is short ("14" -> "14:00 to 14:59") */
  title?: string;
  detail?: string;
}

/**
 * Values across an ordered axis (hours, weekdays, months). The tallest column is the point, so it alone carries the color
 * and the rest are grey (emphasis); pass `emphasis={false}` to color them all.
 */
export function Columns({ data, format = fmtInt, valueName = 'Total', labelEvery = 1, height = 190, emphasis = true, emptyText }: { data: ColumnPoint[]; format?: (n: number) => string; valueName?: string; labelEvery?: number; height?: number; emphasis?: boolean; emptyText?: string }) {
  const [ref, width] = useWidth();
  const { setTip, clear, node } = useTip(width);
  const max = Math.max(0, ...data.map((d) => d.value));
  if (data.length === 0 || max === 0) return <Empty>{emptyText}</Empty>;

  const ticks = niceTicks(max, 3, data.every((d) => Number.isInteger(d.value)));
  const top = ticks[ticks.length - 1]!;
  const left = 44;
  const bottom = 24;
  const plotW = Math.max(40, width - left - 6);
  const plotH = height - bottom - 12;
  const slot = plotW / data.length;
  const barW = Math.min(24, Math.max(3, slot - 3));
  const y = (v: number) => 8 + plotH - (v / top) * plotH;
  const peak = data.findIndex((d) => d.value === max);
  // show only as many axis labels as fit side by side (about 6.5 px a character plus a gap), whatever was asked for
  const widest = Math.max(...data.map((d) => d.label.length));
  const every = Math.max(labelEvery, Math.ceil((widest * 6.5 + 10) / slot));

  return (
    <div ref={ref} className="viz-plot">
      {width > 0 ? (
        <svg width={width} height={height} role="img" aria-label={`${valueName} by ${data.length} periods; highest at ${data[peak]!.title ?? data[peak]!.label}`}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={left} x2={width - 6} y1={y(t)} y2={y(t)} className={t === 0 ? 'viz-axis' : 'viz-grid'} />
              <text x={left - 8} y={y(t) + 4} textAnchor="end" className="viz-tick">
                {format(t)}
              </text>
            </g>
          ))}
          {data.map((d, i) => {
            const cx = left + slot * i + slot / 2;
            const h = Math.max(d.value > 0 ? 3 : 0, plotH - (y(d.value) - 8));
            const show = () => setTip({ x: cx, y: y(d.value) - 6, title: d.title ?? d.label, rows: [{ label: valueName, value: format(d.value), color: 'var(--viz-1)' }, ...(d.detail ? [{ label: d.detail, value: '' }] : [])] });
            const r = Math.min(4, barW / 2);
            const fill = !emphasis || i === peak ? 'var(--viz-1)' : 'var(--viz-gray)';
            return (
              <g key={d.label + i} tabIndex={0} className="viz-hit" onPointerMove={show} onPointerLeave={clear} onFocus={show} onBlur={clear}>
                <rect x={left + slot * i} y={0} width={slot} height={height - bottom} fill="transparent" />
                {h > 0 ? <path d={`M${cx - barW / 2},${8 + plotH} v${-(h - r)} a${r},${r} 0 0 1 ${r},${-r} h${barW - 2 * r} a${r},${r} 0 0 1 ${r},${r} v${h - r} z`} fill={fill} className="viz-mark" /> : null}
                {i % every === 0 ? (
                  <text x={cx} y={height - 6} textAnchor="middle" className="viz-tick">
                    {d.label}
                  </text>
                ) : null}
              </g>
            );
          })}
          {data[peak]!.value > 0 ? (
            <text x={Math.min(width - 6, Math.max(left, left + slot * peak + slot / 2))} y={y(max) - 5} textAnchor="middle" className="viz-value">
              {format(max)}
            </text>
          ) : null}
        </svg>
      ) : null}
      {node}
    </div>
  );
}

/* ----------------------------------------------------------------------------- line */
export interface LinePoint {
  label: string;
  value: number;
  title?: string;
}

/** One series over time: a 2 px line over a faint wash, a crosshair that finds the nearest day, and a dot on the latest value. */
export function LineChart({ data, format = fmtInt, valueName = 'Total', max, height = 200, emptyText }: { data: LinePoint[]; format?: (n: number) => string; valueName?: string; max?: number; height?: number; emptyText?: string }) {
  const [ref, width] = useWidth();
  const { setTip, clear, node } = useTip(width);
  const [hover, setHover] = useState<number | null>(null);
  const top0 = Math.max(0, ...data.map((d) => d.value));
  if (data.length < 2 || top0 === 0) return <Empty>{emptyText}</Empty>;

  const ticks = niceTicks(max ?? top0, 3, data.every((d) => Number.isInteger(d.value)));
  const top = ticks[ticks.length - 1]!;
  const left = 44;
  const bottom = 24;
  const plotW = Math.max(40, width - left - 12);
  const plotH = height - bottom - 12;
  const x = (i: number) => left + (plotW * i) / (data.length - 1);
  const y = (v: number) => 8 + plotH - (v / top) * plotH;
  const line = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d.value).toFixed(1)}`).join(' ');
  const area = `${line} L${x(data.length - 1).toFixed(1)},${8 + plotH} L${left},${8 + plotH} Z`;
  const xLabels = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * (data.length - 1)));

  const find = (clientX: number) => {
    const box = ref.current!.getBoundingClientRect();
    const i = Math.max(0, Math.min(data.length - 1, Math.round(((clientX - box.left - left) / plotW) * (data.length - 1))));
    setHover(i);
    setTip({ x: x(i), y: y(data[i]!.value), title: data[i]!.title ?? data[i]!.label, rows: [{ label: valueName, value: format(data[i]!.value), color: 'var(--viz-1)' }] });
  };
  const last = data.length - 1;

  return (
    <div ref={ref} className="viz-plot">
      {width > 0 ? (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={`${valueName} over ${data.length} days; latest ${format(data[last]!.value)}`}
          tabIndex={0}
          onPointerMove={(e) => find(e.clientX)}
          onPointerLeave={() => {
            setHover(null);
            clear();
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
              const i = Math.max(0, Math.min(last, (hover ?? last) + (e.key === 'ArrowRight' ? 1 : -1)));
              setHover(i);
              setTip({ x: x(i), y: y(data[i]!.value), title: data[i]!.title ?? data[i]!.label, rows: [{ label: valueName, value: format(data[i]!.value), color: 'var(--viz-1)' }] });
              e.preventDefault();
            }
          }}
          onBlur={() => {
            setHover(null);
            clear();
          }}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line x1={left} x2={width - 12} y1={y(t)} y2={y(t)} className={t === 0 ? 'viz-axis' : 'viz-grid'} />
              <text x={left - 8} y={y(t) + 4} textAnchor="end" className="viz-tick">
                {format(t)}
              </text>
            </g>
          ))}
          <path d={area} fill="var(--viz-1)" opacity={0.1} />
          <path d={line} fill="none" stroke="var(--viz-1)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          {xLabels.map((i, k) => (
            <text key={i} x={x(i)} y={height - 6} textAnchor={k === 0 ? 'start' : k === xLabels.length - 1 ? 'end' : 'middle'} className="viz-tick">
              {data[i]!.label}
            </text>
          ))}
          {hover !== null ? <line x1={x(hover)} x2={x(hover)} y1={8} y2={8 + plotH} className="viz-cross" /> : null}
          <circle cx={x(hover ?? last)} cy={y(data[hover ?? last]!.value)} r={4.5} fill="var(--viz-1)" stroke="var(--surface)" strokeWidth={2} />
        </svg>
      ) : null}
      {node}
    </div>
  );
}

/* ----------------------------------------------------------------------------- split */
export interface Part {
  label: string;
  value: number;
}

/** Part of a whole: one bar divided by 2 px gaps, with every part listed beside its value and share (never color alone). */
export function Split({ parts, format = fmtInt, valueName = 'Total', emptyText }: { parts: Part[]; format?: (n: number) => string; valueName?: string; emptyText?: string }) {
  const [ref, width] = useWidth();
  const { setTip, clear, node } = useTip(width);
  const clipId = useId().replace(/:/g, '');
  const live = parts.filter((p) => p.value > 0);
  const total = live.reduce((s, p) => s + p.value, 0);
  if (total === 0) return <Empty>{emptyText}</Empty>;
  // at most six colored parts; the tail is folded into "Other" (a seventh hue is never invented)
  const shown = live.length > 6 ? [...live.slice(0, 5), { label: 'Other', value: live.slice(5).reduce((s, p) => s + p.value, 0) }] : live;
  const GAP = 2;
  const usable = Math.max(40, width) - GAP * (shown.length - 1);
  let at = 0;
  return (
    <div ref={ref} className="viz-plot">
      {width > 0 ? (
        <svg width={width} height={26} role="img" aria-label={`${valueName}: ${shown.map((p) => `${p.label} ${format(p.value)}`).join(', ')}`}>
          <defs>
            <clipPath id={clipId}>
              <rect x={0} y={3} width={width} height={20} rx={6} />
            </clipPath>
          </defs>
          <g clipPath={`url(#${clipId})`}>
            {shown.map((p, i) => {
              const w = Math.max(3, (p.value / total) * usable);
              const x = at;
              at += w + GAP;
              const show = () => setTip({ x: Math.min(width - 8, x + w / 2), y: 13, title: p.label, rows: [{ label: valueName, value: `${format(p.value)} (${fmtPct((p.value / total) * 100)})`, color: `var(--viz-${i + 1})` }] });
              return <rect key={p.label} x={x} y={3} width={w} height={20} fill={`var(--viz-${i + 1})`} className="viz-mark" tabIndex={0} onPointerMove={show} onPointerLeave={clear} onFocus={show} onBlur={clear} />;
            })}
          </g>
        </svg>
      ) : null}
      <ul className="viz-legend">
        {shown.map((p, i) => (
          <li key={p.label}>
            <span className="viz-key" style={{ background: `var(--viz-${i + 1})` }} />
            <span className="viz-legend-name">{p.label}</span>
            <strong className="num">{format(p.value)}</strong>
            <span className="muted num">{fmtPct((p.value / total) * 100)}</span>
          </li>
        ))}
      </ul>
      {node}
    </div>
  );
}
